import { Injectable } from '@angular/core';
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { TreeNode } from 'primeng/api';
import { PlantWithSectors } from '../models/plant-with-sectors.model';

type StoredData = TreeNode[] | PlantWithSectors[] | string;

interface HierarchyDB extends DBSchema {
  hierarchy: {
    key: string;
    value: {
      key: string;
      data: StoredData;
      timestamp: number;
    };
  };
}

@Injectable({
  providedIn: 'root'
})
export class IndexedDbService {
  private dbName = 'HierarchyDatabase';
  private dbVersion = 1;
  private readonly storeName = 'hierarchy' as const;
  private db: IDBPDatabase<HierarchyDB> | null = null;

  constructor() {
    this.initDB();
  }

  private async initDB(): Promise<void> {
    try {
      this.db = await openDB<HierarchyDB>(this.dbName, this.dbVersion, {
        upgrade: (db: IDBPDatabase<HierarchyDB>) => {
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName, { keyPath: 'key' });
          }
        },
      });
    } catch (error) {
      console.error('Error initializing IndexedDB:', error);
    }
  }

  private async ensureDB(): Promise<IDBPDatabase<HierarchyDB>> {
    if (!this.db) {
      await this.initDB();
    }
    if (!this.db) {
      throw new Error('Failed to initialize database');
    }
    return this.db;
  }

  async setItem(key: string, value: StoredData): Promise<void> {
    try {
      const db = await this.ensureDB();
      await db.put(this.storeName, {
        key,
        data: value,
        timestamp: Date.now(),
      });
    } catch (error) {
      console.error('Error setting item in IndexedDB:', error);
      throw error;
    }
  }

  async getItem(key: string): Promise<StoredData | null> {
    try {
      const db = await this.ensureDB();
      const result = await db.get(this.storeName, key);
      return result ? result.data : null;
    } catch (error) {
      console.error('Error getting item from IndexedDB:', error);
      return null;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      const db = await this.ensureDB();
      await db.delete(this.storeName, key);
    } catch (error) {
      console.error('Error removing item from IndexedDB:', error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    try {
      const db = await this.ensureDB();
      await db.clear(this.storeName);
    } catch (error) {
      console.error('Error clearing IndexedDB:', error);
      throw error;
    }
  }
}
